'use client';

import { useState } from 'react';
import { useTranslation } from '../lib/i18n';
import { getFormatLocale } from '../lib/useNationalEnvironment';
import UserModal from './UserModal';

type AuditInfoLineProps = {
  createdAt?: string | null;
  modifiedAt?: string | null;
  createdByName?: string | null;
  createdById?: number | null;
  modifiedByName?: string | null;
  modifiedById?: number | null;
  /** Compact layout (inline) vs full layout (block, used in detail panels). */
  variant?: 'inline' | 'block';
  /** Optional label prefix for the modified line. */
  modifiedLabel?: string;
  createdLabel?: string;
};

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  try {
    const d = new String(value).includes('T') ? new Date(value) : new Date(value + 'Z');
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString(getFormatLocale(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(value);
  }
}

/**
 * Reusable audit info display: shows createdAt/modifiedAt with author names
 * as clickable links that open a UserModal. Used across all entity agendas.
 */
export default function AuditInfoLine({
  createdAt,
  modifiedAt,
  createdByName,
  createdById,
  modifiedByName,
  modifiedById,
  variant = 'block',
  modifiedLabel,
  createdLabel,
}: AuditInfoLineProps) {
  const { t } = useTranslation();
  const _modifiedLabel = modifiedLabel ?? t('armorikModified');
  const _createdLabel = createdLabel ?? t('armorikCoaDetailFieldCreated');
  const [userModalId, setUserModalId] = useState<number | null>(null);

  const renderName = (name: string | null | undefined, id: number | null | undefined) => {
    if (!name) return null;
    if (id != null) {
      return (
        <button
          type="button"
          onClick={() => setUserModalId(id)}
          className="text-moss hover:underline"
        >
          {name}
        </button>
      );
    }
    return <span className="text-wood">{name}</span>;
  };

  if (variant === 'inline') {
    return (
      <>
        <span className="text-bark">
          {_modifiedLabel}: {formatDateTime(modifiedAt)}
          {modifiedByName && <span className="text-wood"> ({renderName(modifiedByName, modifiedById)})</span>}
        </span>
        <UserModal userId={userModalId} onClose={() => setUserModalId(null)} />
      </>
    );
  }

  return (
    <div className="pt-3 border-t border-wheat/30 space-y-1 text-xs">
      <div>
        <span className="text-xs font-medium text-wood">{_createdLabel}:</span>{' '}
        <span className="text-bark">{formatDateTime(createdAt)}</span>
      </div>
      {createdByName && (
        <div>
          <span className="text-xs font-medium text-wood">{t('auditCreatedBy')}</span>{' '}
          {renderName(createdByName, createdById)}
        </div>
      )}
      <div>
        <span className="text-xs font-medium text-wood">{_modifiedLabel}:</span>{' '}
        <span className="text-bark">{formatDateTime(modifiedAt)}</span>
      </div>
      {modifiedByName && (
        <div>
          <span className="text-xs font-medium text-wood">{t('auditModifiedBy')}</span>{' '}
          {renderName(modifiedByName, modifiedById)}
        </div>
      )}
      <UserModal userId={userModalId} onClose={() => setUserModalId(null)} />
    </div>
  );
}
